import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { TerminalShell } from "@/components/TerminalShell";
import { paymentService } from "@/services/paymentService";
import { tradingService } from "@/services/marketService";
import { errorMessage } from "@/services/apiClient";
import { fmtDate, fmtMoney } from "@/lib/format";
import { useLanguage } from "@/context/LanguageContext";

const input = "h-11 w-full rounded-md border border-[#1D2637] bg-[#0E1523] px-3 text-sm text-white outline-none focus:border-[#2563EB]";
const STATUS = { pending: "text-[#F59E0B]", approved: "text-[#10B981]", rejected: "text-[#EF4444]" };
const TYPES = [{ v: "btc", l: "BTC" }, { v: "usdt", l: "USDT" }, { v: "pix", l: "PIX" }, { v: "card", l: "Cartão" }, { v: "iban", l: "IBAN (transferência bancária)" }];
const DETAILS_PLACEHOLDER = (t, type) => ({
  btc: `${t("deposit.wallet")} BTC`,
  usdt: `${t("deposit.wallet")} USDT`,
  pix: t("deposit.pixKey"),
  card: `${t("withdraw.accountName")}`,
  iban: "IBAN (ex: PT50 0002 0123 1234 5678 9015 4)",
}[type]);

export default function WithdrawPage() {
  const { t } = useLanguage();
  const [accounts, setAccounts] = useState([]);
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({ type: "btc", label: "", details: "", network: "" });
  const [request, setRequest] = useState({ account_id: "", amount: "" });

  const load = useCallback(async () => {
    try {
      const [a, h, s] = await Promise.all([
        paymentService.accounts(), paymentService.withdrawals(), tradingService.summary(),
      ]);
      setAccounts(a);
      setHistory(h);
      setSummary(s);
      setRequest((r) => ({ ...r, account_id: r.account_id || a[0]?.id || "" }));
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addAccount = async (e) => {
    e.preventDefault();
    try {
      await paymentService.addAccount(form);
      toast.success(t("withdraw.accountAdded"));
      setForm({ type: "btc", label: "", details: "", network: "" });
      load();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const removeAccount = async (id) => {
    try { await paymentService.deleteAccount(id); load(); } catch (e) { toast.error(errorMessage(e)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await paymentService.createWithdrawal({ account_id: request.account_id, amount: Number(request.amount) });
      toast.success(t("withdraw.sent"));
      setRequest({ ...request, amount: "" });
      load();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <TerminalShell account={summary}>
      <div data-testid="withdraw-page" className="grid grid-cols-1 gap-6 p-4 sm:p-8 xl:grid-cols-2">
        <section className="space-y-5 rounded-md border border-[#141A26] bg-[#0A0E16] p-6">
          <h1 className="font-heading text-2xl font-light tracking-tight text-white">{t("withdraw.title")}</h1>
          <p className="font-mono text-xs text-[#8A97AD]">{t("withdraw.available")} {fmtMoney(summary?.free_margin)}</p>

          <form onSubmit={submit} className="space-y-3" data-testid="withdraw-form">
            <select value={request.account_id} onChange={(e) => setRequest({ ...request, account_id: e.target.value })}
                    data-testid="withdraw-account-select" className={input}>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.label} · {a.type.toUpperCase()}</option>)}
            </select>
            <input type="number" min="0" step="0.01" required value={request.amount}
                   onChange={(e) => setRequest({ ...request, amount: e.target.value })}
                   placeholder={t("withdraw.amount")} data-testid="withdraw-amount-input" className={`${input} font-mono`} />
            <button type="submit" disabled={!accounts.length} data-testid="withdraw-submit"
                    className="h-11 w-full rounded-md bg-[#2563EB] text-xs font-semibold uppercase tracking-[0.15em] text-white hover:bg-[#1D4ED8] disabled:opacity-50">
              {t("withdraw.request")}
            </button>
          </form>

          <div className="space-y-3 border-t border-[#141A26] pt-5">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A97AD]">{t("withdraw.accounts")}</p>
            {accounts.map((a) => (
              <div key={a.id} data-testid={`account-row-${a.id}`} className="flex items-center justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <p className="text-white">{a.label} <span className="font-mono text-xs text-[#5B9CFF]">{a.type.toUpperCase()}</span></p>
                  <p className="truncate font-mono text-xs text-[#8A97AD]">{a.details}</p>
                </div>
                <button onClick={() => removeAccount(a.id)} data-testid={`delete-account-${a.id}`}
                        className="rounded-md border border-[#1D2637] p-2 text-[#8A97AD] hover:text-[#EF4444]">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            <form onSubmit={addAccount} className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="add-account-form">
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                      data-testid="account-type-select" className={input}>
                {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
              <input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                     placeholder={t("withdraw.accountName")} data-testid="account-label-input" className={input} />
              <input required value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })}
                     placeholder={DETAILS_PLACEHOLDER(t, form.type)} data-testid="account-details-input" className={`${input} sm:col-span-2`} />
              {form.type === "iban" ? (
                <input value={form.network || ""} onChange={(e) => setForm({ ...form, network: e.target.value })}
                       placeholder={t("withdraw.bank")} data-testid="account-bank-input" className={`${input} sm:col-span-2`} />
              ) : null}
              <button type="submit" data-testid="add-account-submit"
                      className="flex h-11 items-center justify-center gap-2 rounded-md border border-[#1D2637] text-xs uppercase tracking-[0.15em] text-[#8A97AD] hover:text-white sm:col-span-2">
                <Plus className="h-4 w-4" /> {t("withdraw.addAccount")}
              </button>
            </form>
          </div>
        </section>

        <section className="rounded-md border border-[#141A26] bg-[#0A0E16] p-6">
          <h2 className="font-heading text-lg font-light tracking-tight text-white">{t("withdraw.history")}</h2>
          <div className="mt-4 space-y-2" data-testid="withdraw-history">
            {history.map((w) => (
              <div key={w.id} data-testid={`withdrawal-row-${w.id}`}
                   className="flex items-center justify-between border-b border-[#101724] py-3 text-sm">
                <div>
                  <p className="font-mono text-white">{fmtMoney(w.amount)}</p>
                  <p className="text-xs text-[#8A97AD]">{w.account_label} · {fmtDate(w.created_at)}</p>
                </div>
                <span className={`font-mono text-xs uppercase ${STATUS[w.status]}`}>{t(`status.${w.status}`)}</span>
              </div>
            ))}
            {history.length === 0 ? <p className="py-8 text-center text-sm text-[#5C6B82]">{t("withdraw.empty")}</p> : null}
          </div>
        </section>
      </div>
    </TerminalShell>
  );
}
